/**
 * Per-level readouts for a plan view: the block describing the shown bench and the
 * per-category counts its legend appends.
 *
 * Both are derived from the rows *currently drawn*, so they track whichever level the
 * chart is filtered to. They used to be computed by ChartCard and spliced into the
 * config it handed the renderer, which meant the report — rendering through
 * ChartRenderer directly, without a card — showed the bench with no indication of
 * which bench it was, and a legend without counts (US 2097).
 *
 * Living here, applied by normalizeConfig, they reach the card, the report preview and
 * the PNG export through the one path all three share.
 *
 * Split across the two surfaces that already exist, and NOT into a stats box:
 *
 *   - the BENCH names the subtitle — it answers "which bench am I looking at", which
 *     belongs with the figure's own title;
 *   - the BLOCK COUNT and ACCURACY head the legend, beside the per-class counts they
 *     are computed from.
 *
 * The stats box that used to carry all three is gone. It was a third surface repeating
 * what the other two could say, and it was expensive: its gutter is reserved by
 * shrinking `xaxis.domain`, which is what forces `constrain: 'range'` on the axes —
 * and under 'range' the drawn range is widened from the plot area in PIXELS, so every
 * change in available width moved the frame. Without the box the plan view keeps
 * `constrain: 'domain'` and the frame holds still (measured: the X range is identical
 * across benches and across window widths).
 *
 * The subtitle does cost height — it feeds the title block, which pushes the plot area
 * down — and its width now changes with the bench. That is a deliberate trade, made
 * with the numbers on the table.
 */

import type { DataPoint } from '../../types/dashboard';
import type { ChartOptions } from '../model';

export interface LevelViewInput {
  /** Rows of the shown level — already subset by the chart's filters. */
  data: DataPoint[];
  /**
   * Every row of the dataset, shown level or not. The category set is taken from
   * here so a level missing one still counts it (as zero) and keeps its legend
   * entry — a legend that changes size between levels moves the plot area, and with
   * it the axes. Defaults to `data` when the chart is unfiltered.
   */
  allData?: DataPoint[];
  /** The shown level's value, from the chart's first categorical filter. */
  level: string | undefined;
  /** Category column whose values the legend lists. */
  groupKey?: string;
  options?: ChartOptions;
}

export interface LevelView {
  /** The shown bench, appended to the figure's subtitle. */
  benchLabel?: string;
  /** Block count and accuracy, drawn as the legend's heading. */
  legendHeading?: string;
  legendCounts?: Record<string, number>;
}

export function resolveLevelView({
  data,
  allData,
  level,
  groupKey,
  options,
}: LevelViewInput): LevelView {
  if (!options?.levelSelector || level === undefined || data.length === 0) return {};

  const counts: Record<string, number> = {};
  if (groupKey) {
    // Seed every category the dataset knows at zero, then count the shown level over
    // it — so an absent category reads "— 0" instead of silently losing its entry.
    for (const row of allData ?? data) counts[String(row[groupKey])] = 0;
    for (const row of data) {
      const key = String(row[groupKey]);
      counts[key] = (counts[key] ?? 0) + 1;
    }
  }

  // Only name metres when the level carries a unit — a bare grid index has none, and
  // labelling it "z" in metres would invent a coordinate the reader takes at face value.
  const unit = options.levelSelectorUnit;
  const benchLabel = unit
    ? `Banco ${Number(level).toLocaleString('es-CL')} ${unit}`
    : `Banco (índice) ${level}`;

  // One line, not a label/value table: it heads a legend, where a table has no columns
  // to line up in. The unit rides with the number ("3 bloques") so the line reads as a
  // sentence fragment rather than as a stray figure.
  const heading = [`${data.length.toLocaleString('es-CL')} bloques`];
  const accuracyClasses = options.accuracyClasses;
  if (accuracyClasses) {
    const correct = accuracyClasses.reduce((sum, c) => sum + (counts[c] ?? 0), 0);
    // Just "Accuracy": which classes count as correct is spelled out by the entries
    // right below it.
    heading.push(`Accuracy ${((correct / data.length) * 100).toFixed(1)} %`);
  }

  return {
    benchLabel,
    legendHeading: heading.join(' · '),
    // An explicit `legendCounts` is the consumer's override — don't recompute over it.
    ...(groupKey && !options.legendCounts ? { legendCounts: counts } : {}),
  };
}
